import { Button } from "@sellar-io/components";
import {
  OrderFulfillmentType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import {
  CREATE_CONSIGNMENT,
  NEW_COLLECTION,
  ORDER_DETAILS,
} from "../../constants/routes";
import { useSellarUser } from "../../hooks";
import { useOrderIntegrationValueList } from "../../hooks/api/v2/order/useOrderIntegrationValueList";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { OrderContentConnectionsType } from "../../pages/order/OrderContent/OrderContent";
import { Alert } from "../alert/Alert";
import { OrderCollectionStatus } from "./OrderCollectionStatus/OrderCollectionStatus";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { DX_CUTOFF_COPY } from "../../constants/dxCutoff.shared";

export const OrderShipmentStates = ({
  order,
  connection,
}: {
  order: OrderGetResponseType;
  connection?: OrderContentConnectionsType;
}) => {
  const { user: sellarUser } = useSellarUser();
  const orderId = order.id;
  const fulfillmentType = order.fulfillmentType;
  const apcEnabled = useRemoteFeatureFlags().isActive(
    "enableApcFulfillmentType",
  );
  if (fulfillmentType === "ups" || (apcEnabled && fulfillmentType === "apc"))
    return (
      <UpsOrderShipmentStates
        orderId={orderId}
        fulfillmentType={fulfillmentType}
      />
    );

  if (fulfillmentType === "dx" || fulfillmentType === "dxDropOff") {
    const typeformUrl = "https://sellar.typeform.com/to/REBOh4ob#";
    const supplierName = order.Supplier.Business.name;
    const buyerName = order.Retailer.Business.name;
    const deliveryAddress = `${order.DeliveryAddress.name}, ${order.DeliveryAddress.street}, ${order.DeliveryAddress.city}, ${order.DeliveryAddress.postalCode}`;
    const email = order.Supplier.Business.email;
    const phoneNumber = order.Supplier.Business.phoneNumber;
    const orderId = order.id;
    const collectionAddress = `${order.Supplier.Business.CollectionAddress.name}, ${order.Supplier.Business.CollectionAddress.street}, ${order.Supplier.Business.CollectionAddress.city}, ${order.Supplier.Business.CollectionAddress.postalCode}`;
    const requestedByName = order.Supplier.Business.name;
    const requestedByEmail = order.Supplier.Business.email;
    const supplierPhone = sellarUser?.smsNumber;
    const buyerPhone = order.retailerContactSmsNumber;
    const buyerEmail = connection?.buyerContactEmail ?? "";
    const url = [
      typeformUrl,
      "fulfillment_type=",
      fulfillmentType,
      "&supplier_name=",
      supplierName,
      "&buyer_name=",
      buyerName,
      "&delivery_address=",
      deliveryAddress,
      "&email=",
      email,
      "&phone_number=",
      phoneNumber,
      "&order_id=",
      orderId,
      "&collection_address=",
      collectionAddress,
      "&requested_by_name=",
      requestedByName,
      "&requested_by_email=",
      requestedByEmail,
      "&supplier_phone=",
      supplierPhone,
      "&buyer_phone=",
      buyerPhone,
      "&buyer_email=",
      buyerEmail,
    ]
      .filter(Boolean)
      .join("");
    return (
      <Alert
        className={"w-full"}
        type={"warning"}
        message={
          <>
            If you haven&apos;t already done so, use the link below to request
            your shipping labels
            <span className="mt-2 block font-semibold">
              {DX_CUTOFF_COPY[fulfillmentType]}
            </span>
          </>
        }
        title={"Action required. Request your labels"}
      >
        <div className="flex flex-row gap-2">
          <Button
            color={"secondary"}
            className={"border-orange-300 bg-orange-100 text-orange-800"}
            onClick={() => window.open(url, "_blank")}
          >
            Request labels
          </Button>
          <Button
            color={"tertiary"}
            className={"text-orange-800"}
            onClick={() =>
              window.open(
                "https://intercom.help/sellario/en/articles/12709597-supplier-guide-dx-hub-shipping-and-prices",
                "_blank",
              )
            }
          >
            View rates
          </Button>
        </div>
      </Alert>
    );
  }

  return null;
};

const UpsOrderShipmentStates = ({
  orderId,
  fulfillmentType,
}: {
  orderId: number;
  fulfillmentType: OrderFulfillmentType;
}) => {
  if (fulfillmentType !== "ups" && fulfillmentType !== "apc")
    throw new Error("Only ups or apc shipping allowed.");

  const collectionsV2Enabled =
    useRemoteFeatureFlags().isActive("collectionsV2");

  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderIntegrationValueQuery = useOrderIntegrationValueList(
    {
      orderId: orderId,
    },
    {
      enabled:
        (fulfillmentType === "ups" || fulfillmentType === "apc") &&
        !!business?.Supplier,
    },
  );
  if (
    orderIntegrationValueQuery.isLoading &&
    orderIntegrationValueQuery.fetchStatus !== "idle"
  )
    return null;
  return (
    <>
      {searchParams.get("shippingSuccess") && (
        <Alert
          className={"w-full"}
          type={"success"}
          title={"Shipment created"}
          message={"Your shipping labels are available."}
          onDismiss={() => {
            navigate(ORDER_DETAILS.href({ orderId }));
          }}
        />
      )}
      {!!orderIntegrationValueQuery.data?.pages[0]?.length ? (
        <>
          {fulfillmentType === "ups" &&
            (collectionsV2Enabled ? (
              <OrderCollectionStatus orderId={orderId} />
            ) : (
              <>
                {fulfillmentType === "ups" ? (
                  <Alert
                    className={"w-full"}
                    message={
                      "You will need to schedule a collection. You can include multiple shipments in a single collection. If you have already done this, please ignore this message."
                    }
                    title={"Have you booked your collection?"}
                    type={"information"}
                  >
                    <Button
                      color={"secondary"}
                      className={"border-blue-300 bg-blue-100 text-blue-800"}
                      onClick={() => navigate(NEW_COLLECTION)}
                    >
                      Book Collection
                    </Button>
                  </Alert>
                ) : null}
              </>
            ))}
        </>
      ) : (
        <Alert
          className={"w-full"}
          message={"Do this now to generate your shipping labels."}
          title={"Action required. Create your shipment."}
          type={"warning"}
        >
          <Button
            color={"secondary"}
            className={"border-orange-300 bg-orange-100 text-orange-800"}
            onClick={() => navigate(CREATE_CONSIGNMENT.href({ orderId }))}
          >
            Create Shipment
          </Button>
        </Alert>
      )}
    </>
  );
};
